export type IconPlatform = "ios" | "android" | "both";
export type IconStyle =
  | "flat"
  | "gradient"
  | "3d"
  | "minimalist"
  | "illustrative"
  | "geometric";
export type IconMood = "professional" | "playful" | "elegant" | "bold" | "calm";

export interface IconSize {
  size: number;
  name: string;
  file: string;
  platform: "ios" | "android" | "capacitor";
  density?: string;
  type?: string;
}

export interface GeneratedIcon {
  iconId: string;
  appId: string;
  userId: string;
  baseImageUrl: string;
  sourceAssetId?: string;
  sourceStoragePath?: string;
  prompt: string;
  style: IconStyle;
  mood: IconMood;
  colorScheme: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  platform: IconPlatform;
  metadata: {
    generatedAt: Date;
    aiModel: string;
    provider: "gemini" | "openai";
  };
  rating?: number;
  approved?: boolean;
}

export interface CreateIconDTO {
  appId: string;
  appName: string;
  appDescription: string;
  appCategory?: string;
  appPlatform?: "android" | "ios" | "both";
  appStoreUrl?: string;
  androidStoreUrl?: string;
  iosStoreUrl?: string;
  researchContext?: import("./app").AppResearchContext;
  keywords: string[];
  style: IconStyle;
  mood: IconMood;
  styleMode?: "manual" | "ai";
  aiStylePrompt?: string;
  aiSuggestedStyle?: IconStyle;
  aiSuggestedMood?: IconMood;
  colorScheme: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  symbols?: string[];
  avoidElements?: string[];
  platform: IconPlatform;
  targetPlatform?: IconPlatform;
  model?: string;
}

export interface IconCropSettings {
  offsetX: number; // -100 to 100
  offsetY: number; // -100 to 100
  zoom: number; // 1 to 3
}

export interface GeneratedIconVariant {
  platform: "ios" | "android" | "capacitor";
  size: number;
  name: string;
  fileName: string;
  dataUrl: string;
}

export interface GooglePlayIconValidationIssue {
  severity: "error" | "warning";
  field: string;
  message: string;
}

export interface GooglePlayIconValidationResponse {
  success: boolean;
  appId: string;
  iconId: string;
  editId?: string;
  packageName: string;
  locale: string;
  playLocale: string;
  localeWasMapped?: boolean;
  status: "success" | "missing" | "error";
  current: {
    count: number;
    images: Array<{
      url: string;
      sha1?: string;
      sha256?: string;
    }>;
  };
  proposed: {
    iconUrl: string;
    sourceAssetId?: string;
    style?: string;
    mood?: string;
    aiModel?: string;
    width?: number;
    height?: number;
    format?: string;
    sizeBytes?: number;
  };
  validationIssues: GooglePlayIconValidationIssue[];
  message?: string;
}

export interface GooglePlayIconPublishResponse {
  success: boolean;
  appId: string;
  iconId: string;
  editId?: string;
  packageName: string;
  locale: string;
  playLocale: string;
  localeWasMapped?: boolean;
  commitStatus: "committed" | "not_committed";
  uploadedCount?: number;
  lastPublishedAt?: string;
  message?: string;
}

export const iOS_ICON_SIZES: IconSize[] = [
  { size: 1024, name: "App Store", file: "AppIcon-1024.png", platform: "ios" },
  {
    size: 180,
    name: "iPhone (3x)",
    file: "AppIcon-60@3x.png",
    platform: "ios",
  },
  {
    size: 120,
    name: "iPhone (2x)",
    file: "AppIcon-60@2x.png",
    platform: "ios",
  },
  { size: 167, name: "iPad Pro", file: "AppIcon-83.5@2x.png", platform: "ios" },
  { size: 152, name: "iPad (2x)", file: "AppIcon-76@2x.png", platform: "ios" },
  { size: 76, name: "iPad", file: "AppIcon-76.png", platform: "ios" },
  {
    size: 40,
    name: "Spotlight (2x)",
    file: "AppIcon-20@2x.png",
    platform: "ios",
  },
  {
    size: 60,
    name: "Spotlight (3x)",
    file: "AppIcon-20@3x.png",
    platform: "ios",
  },
  {
    size: 58,
    name: "Settings (2x)",
    file: "AppIcon-29@2x.png",
    platform: "ios",
  },
  {
    size: 87,
    name: "Settings (3x)",
    file: "AppIcon-29@3x.png",
    platform: "ios",
  },
];

export const ANDROID_ICON_SIZES: IconSize[] = [
  {
    size: 512,
    name: "Play Store",
    file: "ic_launcher.png",
    platform: "android",
    density: "xxxhdpi",
  },
  {
    size: 192,
    name: "xxxhdpi",
    file: "mipmap-xxxhdpi/ic_launcher.png",
    platform: "android",
    density: "xxxhdpi",
  },
  {
    size: 144,
    name: "xxhdpi",
    file: "mipmap-xxhdpi/ic_launcher.png",
    platform: "android",
    density: "xxhdpi",
  },
  {
    size: 96,
    name: "xhdpi",
    file: "mipmap-xhdpi/ic_launcher.png",
    platform: "android",
    density: "xhdpi",
  },
  {
    size: 72,
    name: "hdpi",
    file: "mipmap-hdpi/ic_launcher.png",
    platform: "android",
    density: "hdpi",
  },
  {
    size: 48,
    name: "mdpi",
    file: "mipmap-mdpi/ic_launcher.png",
    platform: "android",
    density: "mdpi",
  },
  {
    size: 432,
    name: "Foreground",
    file: "ic_launcher_foreground.png",
    platform: "android",
    type: "foreground",
  },
  {
    size: 432,
    name: "Background",
    file: "ic_launcher_background.png",
    platform: "android",
    type: "background",
  },
];

export const CAPACITOR_ICON_SIZES: IconSize[] = [
  {
    size: 1024,
    name: "Capacitor Logo",
    file: "capacitor-assets/assets/logo.png",
    platform: "capacitor",
  },
  {
    size: 1024,
    name: "Capacitor Icon Only",
    file: "capacitor-assets/assets/icon-only.png",
    platform: "capacitor",
  },
  {
    size: 1024,
    name: "Capacitor Icon Foreground",
    file: "capacitor-assets/assets/icon-foreground.png",
    platform: "capacitor",
  },
  {
    size: 1024,
    name: "Capacitor Icon Background",
    file: "capacitor-assets/assets/icon-background.png",
    platform: "capacitor",
    type: "background",
  },
];
